import {Injectable} from '@angular/core';
import {Observable, Subscription} from 'rxjs';
import {ApiValidation} from '../../model/api/api-validation';
import {ClearingSelectionSummary} from '../../model/clearing/clearing-selection-summary';
import {ClearingSelectionInfo} from '../../model/clearing/clearing-selection-info';
import {ClearingInfoLoadRequest} from '../../model/clearing/clearing-info-load-request';
import {Trade} from "../../model/clearing/trade";
import Side = Trade.Side;
import {AppConfigService} from "../app-config.service";

@Injectable({providedIn: 'root'})
export class ClearingSelectionService {

  private readonly minItemCountForSummary = 2;

  constructor(private appConfigService: AppConfigService) {
  }

  /**
   * Load summary info if item count is above threshold
   */
  loadFooterInfos(
    itemIds: string[],
    previousSummaryRequest: ClearingInfoLoadRequest | undefined,
    loader: (req: ClearingInfoLoadRequest) => Observable<ApiValidation<ClearingSelectionSummary[]>>,
    updateSummary: (buy: ClearingSelectionSummary, sell: ClearingSelectionSummary) => void,
    triggerChange: () => void,
    setLoading: (isLoading: boolean) => void
  ): Subscription | undefined {
    if (!this.needsSummaryReload(itemIds, previousSummaryRequest)) return undefined;

    const summaryRequest = new ClearingInfoLoadRequest(itemIds);
    const buySummary = new ClearingSelectionSummary(Side.BUY);
    const sellSummary = new ClearingSelectionSummary(Side.SELL);

    updateSummary(buySummary, sellSummary);
    triggerChange();

    if (itemIds.length >= this.minItemCountForSummary
      && itemIds.length <= this.appConfigService.selectionSummaryHardLimitCount) {
      setLoading(true);
      return loader(summaryRequest).subscribe(apiValidation => {
        if (apiValidation.success) {
          const buy = apiValidation.item.find(v => v.side === Side.BUY) ?? buySummary;
          const sell = apiValidation.item.find(v => v.side === Side.SELL) ?? sellSummary;
          updateSummary(buy, sell);
        }
        setLoading(false);
        triggerChange();
      });
    } else {
      buySummary.selectionCount = itemIds.length;
      updateSummary(buySummary, sellSummary);
      setLoading(false);
    }
    return undefined;
  }

  /**
   * Load detailed leg info per selection
   */
  loadSelectionInfos(
    itemIds: string[],
    loader: (req: ClearingInfoLoadRequest) => Observable<ApiValidation<ClearingSelectionInfo[]>>,
    update: (infos: ClearingSelectionInfo[]) => void,
    triggerChange: () => void
  ): Subscription {
    if (itemIds.length <= this.appConfigService.allocationHardLimitCount) {
      const req = new ClearingInfoLoadRequest(itemIds);
      return loader(req).subscribe(apiValidation => {
        if (apiValidation.success) {
          update(apiValidation.item);
          triggerChange();
        }
      });
    }
    return undefined;
  }

  private needsSummaryReload(
    itemIds: string[],
    summaryRequest?: ClearingInfoLoadRequest
  ): boolean {
    return !summaryRequest || JSON.stringify(summaryRequest.itemIds) !== JSON.stringify(itemIds);
  }
}
